import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  signal,
  WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationModalComponent } from '@shared/components/confirmation-modal/confirmation-modal.component';
import { CrudOperation, User } from '@shared/models';
import { AppNotificationService, ModalService } from '@shared/services';
import { CurrentUserService } from '@shared/services/current-user.service';
import { UserService } from '@shared/services/user.service';
import { QueryOptions } from 'odata-query';
import { forkJoin, map, Observable, of, switchMap } from 'rxjs';
import { isGstarSystemUser } from '../../helpers/is-gstar-system-user';
import {
  isSignedInUsersRow,
  signedInSnapshot,
} from '../../helpers/is-signed-in-users-row';
import {
  Client,
  Collection,
  ExternalUser,
  GridItemCreateEvent,
  GridItemUpdateEvent,
  UserEngagementAssignment,
} from '../../models';
import { ClientService, ExternalUserService } from '../../services';

@Component({
  standalone: false,
  selector: 'gstar-external-users-page-component',
  templateUrl: 'external-users-page.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExternalUsersPageComponent {
  private readonly externalUserService = inject(ExternalUserService);
  private readonly appNotificationService = inject(AppNotificationService);
  private readonly userService = inject(UserService);
  private readonly translateService = inject(TranslateService);
  private readonly modalService = inject(ModalService);
  private readonly currentUserService = inject(CurrentUserService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly clientService = inject(ClientService);

  public externalUsers: WritableSignal<Collection<ExternalUser>> = signal({
    total: 0,
    items: [],
  });

  usersQuery: Partial<QueryOptions<User>> = {
    filter: { isActive: true },
    orderBy: 'firstName',
  };

  public query: Partial<QueryOptions<ExternalUser>> = {};

  private entityName = this.translateService.instant(
    'admin.dashboard.externalUserTitleNoticationEntity'
  );

  create(externalUserGridItem: GridItemCreateEvent<ExternalUser>): void {
    let isSuccess = false;

    this.externalUserService
      .create(externalUserGridItem.item)
      .pipe(switchMap(() => this.userService.getAllUser(this.usersQuery)))
      .subscribe(() => {
        externalUserGridItem.onSuccess();
        isSuccess = true;

        this.fetchEntities();

        this.appNotificationService.crudNotify(
          CrudOperation.Create,
          this.entityName,
          isSuccess
        );
      });
  }

  update(externalUserGridItem: GridItemUpdateEvent<ExternalUser>): void {
    let isSuccess = false;

    this.externalUserService
      .update(externalUserGridItem.item)
      .pipe(switchMap(() => this.userService.getAllUser(this.usersQuery)))
      .subscribe(() => {
        externalUserGridItem.onSuccess();
        isSuccess = true;
        this.fetchEntities();

        this.appNotificationService.crudNotify(
          CrudOperation.Update,
          this.entityName,
          isSuccess
        );
      });
  }

  queryUpdate(externalUserGridItem: Partial<QueryOptions<ExternalUser>>): void {
    this.query = externalUserGridItem;
    this.fetchEntities();
  }

  onDeleteUserRequested(user: ExternalUser): void {
    const signedIn = signedInSnapshot(
      this.currentUserService.currentUserInfo()
    );

    if (isSignedInUsersRow({ id: user.id, email: user.email }, signedIn)) {
      this.appNotificationService.showTranslatedToast(
        this.translateService.instant(
          'admin.user.deleteUserSelfNotAllowedTitle'
        ),
        this.translateService.instant(
          'admin.user.deleteUserSelfNotAllowedDescription'
        )
      );
      return;
    }

    if (isGstarSystemUser(user)) {
      this.appNotificationService.showTranslatedToast(
        this.translateService.instant(
          'admin.user.deleteGstarSystemUserNotAllowedTitle'
        ),
        this.translateService.instant(
          'admin.user.deleteGstarSystemUserNotAllowedDescription'
        )
      );
      return;
    }

    this.modalService.open(
      ConfirmationModalComponent,
      { appModalId: 'external-user-delete-confirm-modal' },
      {
        title: this.translateService.instant('admin.modal.confirmationTitle'),
        content: this.translateService.instant(
          'admin.user.deleteUserConfirmMessage',
          { fullName: user.fullName }
        ),
        modalId: 'external-user-delete-confirm-modal',
        confirmationFn: () => this.confirmDeleteExternalUser(user),
      }
    );
  }

  private confirmDeleteExternalUser(user: ExternalUser): void {
    this.externalUserService
      .deleteUser(user.id)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap(() => this.userService.getAllUser(this.usersQuery))
      )
      .subscribe({
        next: () => {
          this.fetchEntities();
          this.appNotificationService.crudNotify(
            CrudOperation.Delete,
            this.entityName,
            true
          );
        },
        error: () => {
          this.appNotificationService.crudNotify(
            CrudOperation.Delete,
            this.entityName,
            false
          );
        },
      });
  }

  private fetchEntities(): void {
    /**
     * Fetches external users and clients, enriches the external users with client names, and updates the externalUsers signal.
     */
    forkJoin({
      externalUsers: this.externalUserService.get({
        ...this.query,
        count: true,
      }),
      clients: this.clientService.get(),
    })
      .pipe(
        switchMap(({ externalUsers, clients }) =>
          this.enrichExternalUsers(externalUsers, clients.items)
        )
      )
      .subscribe((externalUserCollection: Collection<ExternalUser>) => {
        this.externalUsers.set(externalUserCollection);
      });
  }

  /**
   * Get the client names with the list of clients.
   * @param externalUsers The collection of external users to be enriched.
   * @param clients The full list of clients to map client names.
   * @returns An observable of the enriched external users collection.
   */
  private enrichExternalUsers(
    externalUsers: Collection<ExternalUser>,
    clients: Client[]
  ): Observable<Collection<ExternalUser>> {
    if (!externalUsers.items.length) {
      return of(externalUsers);
    }

    const clientNames = new Map(
      clients.map(client => [client.clientId, client.name] as const)
    );

    return forkJoin(
      externalUsers.items.map(user =>
        this.externalUserService.getClientAssignments(user.id).pipe(
          map(engagementAssignments => ({
            ...user,
            engagementAssignments: this.mapEngagementAssignments(
              engagementAssignments,
              clientNames
            ),
          }))
        )
      )
    ).pipe(
      map(items => ({
        ...externalUsers,
        items,
      }))
    );
  }

  /**
   * Maps engagement assignments to include client names and sorts them by active status and client name.
   * @param assignments The list of engagement assignments to be mapped.
   * @param clientNames A map of client IDs to client names.
   * @returns The list of mapped and sorted engagement assignments.
   */
  private mapEngagementAssignments(
    assignments: UserEngagementAssignment[],
    clientNames: Map<number, string>
  ): UserEngagementAssignment[] {
    return assignments
      .map(assignment => ({
        ...assignment,
        clientName:
          clientNames.get(assignment.clientId) ?? String(assignment.clientId),
      }))
      .sort((left, right) => {
        if (left.isActive !== right.isActive) {
          return Number(right.isActive) - Number(left.isActive);
        }

        return left.clientName.localeCompare(right.clientName);
      });
  }
}
